import type { ValidationResult } from "@/domain/records/types";
import { SetupConfig } from "@/domain/records/value-objects/SetupConfig";
import { FileTypeDomainService } from "@/domain/upload/services/FileTypeDomainService";

/**
 * Domain Service for Validation Rules
 * Clean Architecture - Domain Layer
 */
export class ValidationRules {
  /**
   * Validate setup configuration
   */
  static validateSetupConfig(config: SetupConfig): ValidationResult {
    const errors: string[] = [];

    // Check report types
    if (config.reportTypes.length === 0) {
      errors.push("At least one report type must be selected");
    }

    // Check custom prompt if Custom is selected
    if (config.reportTypes.includes("Custom")) {
      if (!config.customPrompt || config.customPrompt.trim().length === 0) {
        errors.push(
          "Custom prompt is required when Custom report type is selected"
        );
      } else if (config.customPrompt.trim().length < 10) {
        errors.push("Custom prompt must be at least 10 characters long");
      }
    }

    // Check goals (research context)
    if (!config.goals || config.goals.trim().length === 0) {
      errors.push("Research context is required");
    } else if (config.goals.trim().length < 10) {
      errors.push("Research context must be at least 10 characters long");
    }

    // Check participant count
    if (
      config.participantCount &&
      (config.participantCount < 1 || config.participantCount > 15)
    ) {
      errors.push("Participant count must be between 1 and 15");
    }

    // Check respondent names
    if (config.respondentNames.length > 10) {
      errors.push("Maximum 10 respondents allowed");
    }

    const invalidNames = config.respondentNames.filter(
      (name) => name && name.length > 100
    );
    if (invalidNames.length > 0) {
      errors.push("Respondent names must be less than 100 characters");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate report generation request
   */
  static validateGenerationRequest(request: {
    reportTypes: string[];
    customPrompt?: string;
    goals?: string;
    isWithTranscript?: boolean;
    isWithInsightReport?: boolean;
  }): ValidationResult {
    const errors: string[] = [];

    // Allow empty reportTypes if transcript is selected OR if insight reports are not enabled
    const shouldRequireReportTypes =
      request.isWithInsightReport === true && !request.isWithTranscript;

    if (
      shouldRequireReportTypes &&
      (!request.reportTypes || request.reportTypes.length === 0)
    ) {
      errors.push("At least one report type must be selected");
    }

    if (request.reportTypes?.includes("Custom")) {
      if (!request.customPrompt || request.customPrompt.trim().length === 0) {
        errors.push(
          "Custom prompt is required when Custom report type is selected"
        );
      }
    }

    if (request.goals && request.goals.trim().length < 10) {
      errors.push("Research goals must be at least 10 characters long");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate file upload
   */
  static validateFileUpload(file: {
    size: number;
    type: string;
    name: string;
  }): ValidationResult {
    const errors: string[] = [];
    const maxSize = 500 * 1024 * 1024; // 500MB

    if (file.size > maxSize) {
      errors.push("File size must be less than 500MB");
    }

    // Allowed MIME types (two-level validation)
    const allowedMimeTypes = [
      "audio/mpeg",
      "audio/mp3",
      "audio/x-mpeg3",
      "audio/mp4",
      "audio/x-m4a",
      "audio/wav",
      "audio/x-wav",
      "audio/x-wave",
      "audio/aac",
      "audio/aacp",
      "audio/flac",
      "audio/ogg",
      "audio/vorbis",
      "audio/x-ogg",
      "application/ogg",
      "audio/aiff",
      "audio/x-aiff",
      "audio/amr",
      "audio/3gpp",
      "audio/basic",
      "audio/x-au",
      "audio/x-mpeg",
      "audio/x-mpeg3",
      "video/x-ogm",
      "video/mp4",
      "video/quicktime",
      "video/x-msvideo",
      "video/webm",
      "video/mpeg",
      "video/x-mpeg",
      "video/avi",
      "video/x-m4v",
      "text/plain",
      "text/markdown",
      "text/x-markdown",
      "text/vtt",
      "application/x-subrip",
    ];

    // Check MIME type
    if (!allowedMimeTypes.includes(file.type)) {
      errors.push("Unsupported file type. Allowed: audio, video, text files");
    }

    // Additional check: file extension (more reliable)
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension && !FileTypeDomainService.isSupportedExtension(extension)) {
      errors.push(`Unsupported file extension: .${extension}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate version number
   */
  static validateVersion(version: number): ValidationResult {
    const errors: string[] = [];

    if (!Number.isInteger(version) || version < 1) {
      errors.push("Version must be a positive integer");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}
